import React, { useEffect, createElement } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ErrorBoundary from './components/ui/ErrorBoundary';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Services from './pages/Services';
import MentalCapacity from './pages/Services/MentalCapacity';
import TraumaPTSD from './pages/Services/TraumaPTSD';
import Specialist from './pages/Services/Specialist';
import Privacy from './pages/Policies/Privacy';
import Terms from './pages/Policies/Terms';
import Cancellation from './pages/Policies/Cancellation';
import ForensicRisk from './pages/Services/ForensicRisk';
import Neurodevelopmental from './pages/Services/Neurodevelopmental';
import ClinicalNegligence from './pages/Services/ClinicalNegligence';
import Sitemap from './pages/Sitemap';
import NotFound from './pages/NotFound';
// Google Analytics 4 - inject directly into DOM so Google can detect it
function GoogleAnalytics() {
  useEffect(() => {
    // Only inject once
    if (document.getElementById('gtag-script')) return;
    const gtagScript = document.createElement('script');
    gtagScript.id = 'gtag-script';
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-HT5Z5GWWNY';
    document.head.insertBefore(gtagScript, document.head.firstChild);
    const inlineScript = document.createElement('script');
    inlineScript.id = 'gtag-inline';
    inlineScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-HT5Z5GWWNY');
    `;
    document.head.insertBefore(inlineScript, gtagScript.nextSibling);
  }, []);
  return null;
}
// ScrollToTop component to ensure page loads at the top
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
// Skip to content link for accessibility
function SkipToContent() {
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-secondary focus:text-white focus:rounded-md focus:shadow-lg" data-id="element-1" data-mp="3ngvh83zowun5">
      Skip to main content
    </a>;
}
export function AppRouter() {
  return <BrowserRouter data-id="element-2" data-mp="2zqrb9pz14isz">
      {/* Site-wide head only. Per-page title, description, canonical, Open Graph,
          Twitter card and page schema are set by components/ui/SEO.tsx. */}
      <Helmet data-id="element-3" data-mp="39hvs6v89mdqg">
        <html lang="en-GB" data-id="element-4" data-mp="39hm45d6s6gne" />
        <link rel="preconnect" href="https://fonts.googleapis.com" data-id="element-5" data-mp="39ix7jbwbdmdz" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" data-id="element-6" data-mp="2c2rlotdgeyfz" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Merriweather:wght@400;700&display=swap" rel="stylesheet" data-id="element-7" data-mp="2gzrs55e7u37m" />
        <link rel="icon" href="https://uploadthingy.s3.us-west-1.amazonaws.com/hDXV1rDXM1m2ARqkQ9Hg8F/Medico_Favicon.png" type="image/png" data-id="element-8" data-mp="u5ek6gnmipox" />
        <link rel="preconnect" href="https://uploadthingy.s3.us-west-1.amazonaws.com" crossOrigin="" data-id="element-9" data-mp="k6p0cic62v61" />
        {/* Above-the-fold logo, preloaded to protect largest contentful paint */}
        <link rel="preload" as="image" href="https://uploadthingy.s3.us-west-1.amazonaws.com/hDXV1rDXM1m2ARqkQ9Hg8F/Medico_Favicon.png" data-id="element-10" data-mp="afb3yjfiibh4" />
        <script type="application/ld+json" data-id="element-11" data-mp="1g7m1is30247">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              "name": "Medicolegal Psychiatry",
              "url": "https://medicolegal-psychiatry.co.uk",
              "email": "enquiries@medicolegal-psychiatry.co.uk",
              "areaServed": ["England", "Wales"],
              "serviceType": "Psychiatric expert witness reports",
              "founder": {
                "@type": "Person",
                "name": "Dr Imtiaz Syed",
                "jobTitle": "Independent Psychiatric Expert Witness"
              }
            }
          `}
        </script>
      </Helmet>
      <GoogleAnalytics data-id="element-12" data-mp="18sqgcsxqkuox" />
      <ErrorBoundary data-id="element-13" data-mp="18sgpl34vaji6">
        <SkipToContent data-id="element-14" data-mp="18rykdh088uy4" />
        <div className="flex flex-col min-h-screen" data-id="element-15" data-mp="1dscst05qq6wo">
          <Header data-id="element-16" data-mp="1dt4hdr0x74dg" />
          <ScrollToTop data-id="element-17" data-mp="1dtnumufml4l5" />
          <main id="main-content" className="flex-grow" data-id="element-18" data-mp="yv3ol5vc98nj">
            <Routes data-id="element-19" data-mp="ywn58xkwplpu">
              <Route path="/" element={<Home data-id="element-21" data-mp="13tx0wuyw2tm4" />} data-id="element-20" data-mp="yx6piky14m71" />
              <Route path="/about" element={<About data-id="element-23" data-mp="13uouy1cu8jaa" />} data-id="element-22" data-mp="13tdm3n4jclcb" />
              <Route path="/services" element={<Services data-id="element-25" data-mp="2t8v58pc34ssh" />} data-id="element-24" data-mp="13vzzw4i0rx2z" />
              <Route path="/services/mental-capacity" element={<MentalCapacity data-id="element-27" data-mp="2y750ezxt7te3" />} data-id="element-26" data-mp="2t9mpx545963z" />
              <Route path="/services/trauma-ptsd" element={<TraumaPTSD data-id="element-29" data-mp="mk2tbfemfseo" />} data-id="element-28" data-mp="2y5tyz6ru6xq5" />
              <Route path="/services/specialist" element={<Specialist data-id="element-31" data-mp="hm1fb8w1yja2" />} data-id="element-30" data-mp="ml48nw2o7127" />
              <Route path="/services/forensic-risk" element={<ForensicRisk data-id="element-33" data-mp="wb5z2uihwvka" />} data-id="element-32" data-mp="hmt1jt3rf4no" />
              <Route path="/services/neurodevelopmental" element={<Neurodevelopmental data-id="element-35" data-mp="ricse13gvd5k" />} data-id="element-34" data-mp="wbyw33rnk6p8" />
              <Route path="/services/clinical-negligence" element={<ClinicalNegligence data-id="element-37" data-mp="119qyirpxbb7o" />} data-id="element-36" data-mp="15b2h4dadqw6d" />
              <Route path="/sitemap" element={<Sitemap data-id="element-39" data-mp="11btlt3zoyq8p" />} data-id="element-38" data-mp="118z2xgwbtdhe" />
              <Route path="/faq" element={<FAQ data-id="element-41" data-mp="1f8gwsaohbgxm" />} data-id="element-40" data-mp="1f99teitq4q21" />
              <Route path="/contact" element={<Contact data-id="element-43" data-mp="1aafcxnj8bdl3" />} data-id="element-42" data-mp="1f8qnk0hcls4d" />
              <Route path="/policies/privacy" element={<Privacy data-id="element-45" data-mp="1ab89xwsdyoq1" />} data-id="element-44" data-mp="1aa5kltapouc8" />
              <Route path="/policies/terms" element={<Terms data-id="element-47" data-mp="3h1y0x4dehznm" />} data-id="element-46" data-mp="3h0vcd33jw9av" />
              <Route path="/policies/cancellation" element={<Cancellation data-id="element-49" data-mp="3c21w52wvq470" />} data-id="element-48" data-mp="3h152qrsicih3" />
              <Route path="*" element={<NotFound data-id="element-51" data-mp="3c3mpip7gjh3a" />} data-id="element-50" data-mp="3c33ja5r66h53" />
            </Routes>
          </main>
          <Footer data-id="element-52" data-mp="1fzp0oz7a3nns" />
        </div>
      </ErrorBoundary>
    </BrowserRouter>;
}